import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Alert,
  Spinner,
} from "reactstrap";

import {
  useCreateSuppliersAutoMapMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface SupplierSelectionModalProps {
  isOpen: boolean;
  toggle: () => void;
  purchaseOrder: any;
  onMappingApplied?: () => void;
}

const SupplierSelectionModal: React.FC<SupplierSelectionModalProps> = ({
  isOpen,
  toggle,
  purchaseOrder,
  onMappingApplied,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [isApplyingMapping, setIsApplyingMapping] = useState(false);
  const [mappingResult, setMappingResult] = useState<any>(null);

  // Auto mapping mutation
  const [createSuppliersAutoMap] = useCreateSuppliersAutoMapMutation();

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method);
  };

  const handleApplyMapping = async () => {
    if (!purchaseOrder?.id || !selectedMethod) return;

    setIsApplyingMapping(true);
    setMappingResult(null);
    
    try {
      const requestData = {
        purchase_request_id: parseInt(purchaseOrder.id),
        method: selectedMethod,
      };

      console.log("Applying supplier mapping:", requestData);

      const result = await createSuppliersAutoMap(requestData).unwrap();

      console.log("Supplier mapping result:", result);
      setMappingResult({ success: true, data: result });

      // Notify parent component
      if (onMappingApplied) {
        onMappingApplied();
      }

    } catch (error) {
      console.error("Failed to apply supplier mapping:", error);
      setMappingResult({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      });
    } finally {
      setIsApplyingMapping(false);
    }
  };

  const handleClose = () => {
    setSelectedMethod("");
    setMappingResult(null);
    setIsApplyingMapping(false);
    toggle();
  };

  const handleApplyAndClose = () => {
    handleClose();
  };

  const mappingOptions = [
    {
      id: "lowest_cost",
      title: "Select Suppliers by Low Cost",
      description: "Automatically assign suppliers with the lowest cost for each product",
      icon: "ri-money-dollar-circle-line",
      color: "success",
    },
    {
      id: "previous_order",
      title: "Select Suppliers by Previous Order",
      description: "Assign suppliers based on your previous purchase history",
      icon: "ri-history-line",
      color: "primary",
    }
  ];

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="ri-filter-line align-middle me-2"></i>
        Supplier Auto-Mapping
      </ModalHeader>
      <ModalBody>
        {/* Mapping Results */}
        {mappingResult && (
          <Alert color={mappingResult.success ? "success" : "danger"} className="mb-4">
            <i className={`ri-${mappingResult.success ? 'check' : 'close'}-circle-line align-middle me-2`}></i>
            {mappingResult.success ? (
              <span>
                Supplier mapping applied successfully! {mappingResult.data?.message || 'Suppliers have been assigned based on your selection.'}
              </span>
            ) : (
              <span>
                Failed to apply supplier mapping: {mappingResult.error}
              </span>
            )}
          </Alert>
        )}

        {/* Selection Options */}
        <div className="mb-4">
          <h6 className="mb-3">Choose Auto-Mapping Method</h6>
          <p className="text-muted mb-4">
            Select a method to automatically assign suppliers to all items in this purchase order.
          </p>

          <div className="row g-3">
            {mappingOptions.map((option) => (
              <div key={option.id} className="col-12">
                <Card 
                  className={`cursor-pointer border ${selectedMethod === option.id ? 'border-primary bg-soft-primary' : 'border-light'}`}
                  onClick={() => handleMethodSelect(option.id)}
                >
                  <CardBody>
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <div className={`avatar-sm rounded-circle bg-soft-${option.color} text-${option.color} d-flex align-items-center justify-content-center`}>
                          <i className={`${option.icon} fs-18`}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 me-2">{option.title}</h6>
                          {selectedMethod === option.id && (
                            <i className="ri-checkbox-circle-fill text-primary fs-16"></i>
                          )}
                        </div>
                        <p className="text-muted mb-0 fs-13">
                          {option.description}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <FormGroup check>
                          <Input
                            type="radio"
                            name="mapping-method"
                            value={option.id}
                            checked={selectedMethod === option.id}
                            onChange={(e) => handleMethodSelect(e.target.value)}
                          />
                          <Label check></Label>
                        </FormGroup>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Current Status */}
        {purchaseOrder?.items && (
          <Card className="bg-light">
            <CardBody className="py-2">
              <div className="row text-center">
                <div className="col-4">
                  <div className="fw-semibold text-dark">{purchaseOrder.items.length}</div>
                  <div className="text-muted small">Total Items</div>
                </div>
                <div className="col-4">
                  <div className="fw-semibold text-dark">
                    {purchaseOrder.items.filter((item: any) => item.supplier_name).length}
                  </div>
                  <div className="text-muted small">With Suppliers</div>
                </div>
                <div className="col-4">
                  <div className="fw-semibold text-dark">
                    {purchaseOrder.items.filter((item: any) => !item.supplier_name && !item.is_rfq).length}
                  </div>
                  <div className="text-muted small">Need Suppliers</div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Warning Message */}
        <Alert color="warning" className="mt-3">
          <i className="ri-alert-line align-middle me-2"></i>
          <strong>Note:</strong> This action will automatically assign suppliers to all items that don't have suppliers and are not marked as RFQ. Existing supplier assignments will be overwritten.
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isApplyingMapping}>
          Cancel
        </Button>
        {mappingResult?.success ? (
          <Button color="primary" onClick={handleApplyAndClose}>
            <i className="ri-check-line align-middle me-1"></i>
            Done
          </Button>
        ) : (
          <Button 
            color="primary" 
            onClick={handleApplyMapping}
            disabled={!selectedMethod || isApplyingMapping}
          >
            {isApplyingMapping ? (
              <>
                <Spinner size="sm" className="me-2" />
                Applying Mapping...
              </>
            ) : (
              <>
                <i className="ri-play-line align-middle me-1"></i>
                Apply Auto-Mapping
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default SupplierSelectionModal;